import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewChildren,
    QueryList,
    ViewEncapsulation
} from "@angular/core";
import {FormControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from "@angular/forms";
import {MatPaginator, MatPaginatorModule} from "@angular/material/paginator";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    Observable,
    startWith,
    Subject,
    take,
    takeUntil
} from "rxjs";
import {fuseAnimations} from "@fuse/animations";
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {DestinationsManagementComponent} from '../destinations-management/destinations-management.component';
import {DestinationSmallType, DestinationType} from '../destinations.types';
import {NgxSpinnerComponent, NgxSpinnerService} from 'ngx-spinner';
import {FuseAlertComponent} from '../../../../../@fuse/components/alert';
import {RestFactoryService} from '../../../../core/services/rest-factory.service';
import {NgSelectModule} from '@ng-select/ng-select';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {en_State} from '../../../../../environments/fixed-enum';
import {MatExpansionModule} from '@angular/material/expansion';
import {environment} from "../../../../../environments/environment";
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {NgxQrcodeStylingComponent, Options} from 'ngx-qrcode-styling';
import {MatSnackBar, MatSnackBarModule} from '@angular/material/snack-bar';

export type SkinConfig = {
    name: string;
    desktopUrl: string;
    mobileKey: string | null;
    mainLogo: string | null;
    sideLogo: string | null;
    headerFooterColor: string;
    themeColor: string;
};

export const skins: Record<string, SkinConfig> = {
    global: {
        name: "Kixa",
        desktopUrl: "https://global.mykixa.com/sign-up",
        mobileKey: null,
        mainLogo: "https://global.mykixa.com/images/kixa-blackold.png",
        sideLogo: null,
        headerFooterColor: "rgb(246 246 246)",
        themeColor: "rgb(184 156 76)",
    }
};

interface GeneratedLinks {
    mobileUrl: string;
    desktopUrl: string;
    label: string;
}

@Component({
    selector: "destinations-list",
    templateUrl: "./destinations-list.component.html",
    styleUrls: ['./destinations-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: fuseAnimations,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatPaginatorModule,
        MatSelectModule,
        ReactiveFormsModule,
        DestinationsManagementComponent,
        NgxSpinnerComponent,
        FuseAlertComponent,
        NgSelectModule,
        MatAutocompleteModule,
        MatExpansionModule,
        MatDialogModule,
        MatTabsModule,
        MatTooltipModule,
        NgxQrcodeStylingComponent,
        MatSnackBarModule
    ]
})
export class DestinationsListComponent implements OnInit, OnDestroy {

    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChildren(NgxQrcodeStylingComponent) qrComponents: QueryList<NgxQrcodeStylingComponent>;

    searchForm: UntypedFormGroup;
    newDestinationForm: UntypedFormGroup;
    parentControl = new FormControl(null);

    displayAddDestination: boolean = false;
    loadingMessage: string = "Loading...";
    title: string;
    reviewMode: boolean;
    destination_ID: string;
    apiEndpoint: string;

    userUiType: number = environment.user_ui_type || 3;
    amCampaignId: string = '';

    tableElements: DestinationType[];
    destinationType: DestinationSmallType;
    destinationTypeParent: DestinationSmallType;
    destinationParentsArray: DestinationType[];
    destinationTypesArray: any[];
    destinationParentsArrayObserve: Observable<DestinationType[]>;
    alert: any;
    searchParameters = en_State;
    showList: boolean = false;
    showFilters: boolean = false;

    selectedSkin: SkinConfig | null = null;
    selectedQrBusiness: DestinationType | null = null;
    generatedLinks: GeneratedLinks | null = null;
    isGeneratingQr: boolean = false;

    baseQrConfig: Options = {
        width: 300,
        height: 300,
        type: 'canvas',
        margin: 10,
        data: '',
        image: '',
        qrOptions: {
            typeNumber: 0,
            mode: 'Byte',
            errorCorrectionLevel: 'H'
        },
        imageOptions: {
            hideBackgroundDots: true,
            imageSize: 0.45,
            margin: 5,
            crossOrigin: 'anonymous',
        },
        dotsOptions: { color: '#000000', type: 'square' },
        backgroundOptions: { color: '#ffffff' },
        cornersSquareOptions: { color: '#000000', type: 'square' },
        cornersDotOptions: { color: '#000000', type: 'square' }
    };

    mobileQrConfig: Options = { ...this.baseQrConfig };
    desktopQrConfig: Options = { ...this.baseQrConfig };
    bigMobileQrConfig: Options = { ...this.baseQrConfig, width: 1024, height: 1024 };
    bigDesktopQrConfig: Options = { ...this.baseQrConfig, width: 1024, height: 1024 };

    private readonly pathConfigs = {
        '/content/destinations/list/review': {
            api: '/admin/destination/review/search',
            title: 'Content Review',
            inReview: true
        },
        '/content/destinations/list/registering': {
            api: '/admin/destination/listing/searchregistering',
            title: 'Registration in Progress',
            inReview: true
        },
        '/content/destinations/list/new': {
            api: '/admin/destination/listing/searchnew',
            title: 'New Destinations',
            inReview: true
        },
        '/content/destinations/list/editing': {
            api: '/admin/destination/listing/searchedit',
            title: 'Edit In Progress',
            inReview: true
        },
        '/content/destinations/list/rejected': {
            api: '/admin/destination/listing/searchrejected',
            title: 'Rejected',
            inReview: true
        }
    };

    private readonly API_GET_DESTINATIONS = "/admin/destination/search";
    private readonly API_POST_NEW_DESTINATION = "/admin/destination/add";
    private readonly API_GET_DESTINATION_TYPES = "/admin/destinationtype/getall";
    private readonly API_GET_ADMIN_QR = '/admin/marketing/getdownloadqr';

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private _restFactoryService: RestFactoryService,
        private spinner: NgxSpinnerService,
        private _dialog: MatDialog,
        private _snackBar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.newDestinationForm = this._formBuilder.group({
            id_type: [{value: '', disabled: true}, [Validators.required]],
            ds_name: [null, Validators.required],
            id_parent: [null],
        });

        this.searchForm = this._formBuilder.group({
            name: [''],
            parent: [''],
            keyword: [''],
            type: [-1],
            deleted: [false]
        });

        if (this.userUiType === 4) {
            const accountDetailsStr = localStorage.getItem('ACCOUNT_DETAILS');
            if (accountDetailsStr) {
                try {
                    const accountDetails = JSON.parse(accountDetailsStr);
                    this.amCampaignId = accountDetails.ds_urlparamcode2_campaign || '';
                } catch (e) {
                    this.amCampaignId = '';
                }
            }
        }

        const currentPath = window.location.pathname;
        const config = this.pathConfigs[currentPath];

        if (this.userUiType === 2 || this.userUiType === 4) {
            this.title = "Linked Properties";
            if (config) {
                this.apiEndpoint = config.api;
                this.reviewMode = config.inReview;
            } else {
                this.apiEndpoint = this.API_GET_DESTINATIONS;
                this.reviewMode = false;
            }
        } else {
            if (config) {
                this.apiEndpoint = config.api;
                this.title = config.title;
                this.reviewMode = config.inReview;
            } else {
                this.apiEndpoint = this.API_GET_DESTINATIONS;
                this.title = "Destinations";
                this.reviewMode = false;
            }
        }

        if (currentPath === '/content/destinations/list/review') {
            this.searchForm.get('type').setValue(2);
        }

        this.searchForm.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this._unsubscribeAll)
        ).subscribe(() => {
            if (this._paginator) {
                this._paginator.pageIndex = 0;
            }
            this.searchElements('filter', true);
        });

        this.searchElements(null);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    get deletedControl(): FormControl {
        return this.searchForm.get('deleted') as FormControl;
    }

    onBlur(): void {
        setTimeout(() => {
            const value = this.parentControl.value;
            if (typeof value === 'string' && !this.destinationParentsArray.find(obj => obj.ds_name === value)) {
                this.parentControl.reset();
                this.parentControl.setErrors({'invalid': true});
                this.parentControl.markAsTouched();
            }
        }, 250);
    }

    displayFn(obj: any): string {
        return obj ? obj.ds_name : '';
    }

    selectClick(event: any) {
        this.newDestinationForm.get('id_parent').setValue(event.option.value.id_destination);
    }

    private _filterArray(ds_name: string): any[] {
        const filterValue = ds_name.toLowerCase();
        return this.destinationParentsArray.filter(obj => obj.ds_name.toLowerCase().includes(filterValue));
    }

    searchElements(searcher: string, skipParentSearch: boolean = false): void {
        const searchValues = this.searchForm.getRawValue();
        let data = {
            "ds_name": searchValues.name,
            "ds_parentname": searchValues.parent,
            "ds_keyword": searchValues.keyword,
            "bl_deleted": searchValues.deleted,
            "en_state": searchValues.type,
        };

        const urlParams = new URLSearchParams(window.location.search);
        const idType = urlParams.get('id_type');
        if (idType !== null) {
            data["id_destinationtype"] = idType;
        }

        if (this.reviewMode) {
            data["id_destinationtype"] = "";
        }

        const payload = this._restFactoryService.createPayloadPaginator(searcher, this._paginator, data, 20);
        this.tableElements = [];
        this.loadingMessage = null;
        this.spinner.show().then();

        this._restFactoryService.httpPostService(this.apiEndpoint, payload)
            .pipe(
                takeUntil(this._unsubscribeAll),
                finalize(() => this.spinner.hide().then()),
                catchError((err) => {
                    this.loadingMessage = "No results found!";
                    this.tableElements = [];
                    this._changeDetectorRef.markForCheck();
                    return err;
                })
            )
            .subscribe((response) => {
                this.tableElements = response.ar_results;
                this.destinationType = response.destinationType;

                const currentPath = window.location.pathname;
                if (!this.pathConfigs[currentPath] && this.destinationType) {
                    if (this.userUiType !== 2 && this.userUiType !== 4) {
                        this.title = response.destinationType.ds_name;
                    }
                }

                if (this._paginator) {
                    this._paginator.length = response.vi_TotalRows;
                }

                if (this.tableElements.length === 0) {
                    this.loadingMessage = "No results found";
                }
                this.destinationTypeParent = response.destinationTypeParent;

                if (!this.destinationTypesArray?.length) this.getDestinationTypes();
                if (!skipParentSearch && this.destinationType) {
                    this.newDestinationForm.get('id_type').setValue(this.destinationType.id_destinationtype);
                    this.searchParent();
                }
                this._changeDetectorRef.markForCheck();
            });
    }

    getDestinationTypes(): void {
        const cached = localStorage.getItem("destinationTypesArray");
        if (cached) {
            this.destinationTypesArray = JSON.parse(cached);
            this._changeDetectorRef.markForCheck();
            return;
        }
        this._restFactoryService.httpPostService(this.API_GET_DESTINATION_TYPES, null)
            .subscribe((response) => {
                this.destinationTypesArray = response.ar_results;
                localStorage.setItem("destinationTypesArray", JSON.stringify(response.ar_results));
                this._changeDetectorRef.markForCheck();
            });
    }

    searchParent(): void {
        const data = {"id_destinationtype": this.destinationTypeParent.id_destinationtype};
        const payload = this._restFactoryService.createPayloadPaginator(null, null, data);

        this._restFactoryService.httpPostService(this.API_GET_DESTINATIONS, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.destinationParentsArray = response.ar_results;
                this.destinationParentsArrayObserve = this.parentControl.valueChanges.pipe(
                    startWith(''),
                    map(value => (typeof value === 'string' ? value : value?.ds_name) || ''),
                    map(name => name ? this._filterArray(name) : this.destinationParentsArray.slice())
                );
                this._changeDetectorRef.markForCheck();
            });
    }

    handleCommand(command: string) {
        this.destination_ID = null;
        this.displayAddDestination = false;
        this.showList = !this.showList;
        if (command) {
            this.searchElements('filter', true);
        }
        this._changeDetectorRef.markForCheck();
    }

    switchToDestination(dest: string): void {
        this.destination_ID = dest;
        this.displayAddDestination = false;
        this.showList = !this.showList;
        this._changeDetectorRef.detectChanges();
    }

    removeHtmlTags(str: string): string {
        return str ? str.replace(/<[^>]*>/g, '') : '';
    }

    createDestination(event: Event): void {
        event.preventDefault();
        if (this.newDestinationForm.invalid) return;

        const payload = new FormData();
        payload.append("ds_name", this.newDestinationForm.get('ds_name').value);
        payload.append("id_destinationtype", this.newDestinationForm.get('id_type').value);
        if (this.newDestinationForm.get('id_parent').value) {
            payload.append("id_destinationparent", this.newDestinationForm.get('id_parent').value);
        }

        this._restFactoryService.httpPostService(this.API_POST_NEW_DESTINATION, payload)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((response) => {
                this.searchForm.get('keyword').setValue(this.newDestinationForm.get('ds_name').value);
                this.switchToDestination(response.id_destination);
            });
    }

    getStateIndicatorColor(state: string): string {
        switch (state) {
            case 'New': return 'bg-cyan-500';
            case 'Editing': return 'bg-indigo-500';
            case 'Draft': return 'bg-slate-500';
            case 'Approved': return 'bg-teal-500';
            case 'Production': return 'bg-emerald-500';
            case 'Rejected': return 'bg-red-500';
            case 'In Review': return 'bg-amber-500';
            default: return 'bg-gray-400';
        }
    }

    getStateBadgeClass(state: string): string {
        switch (state) {
            case 'New': return 'text-cyan-800 bg-cyan-100 dark:text-cyan-100 dark:bg-cyan-900';
            case 'Editing': return 'text-indigo-800 bg-indigo-100 dark:text-indigo-100 dark:bg-indigo-900';
            case 'Draft': return 'text-slate-800 bg-slate-100 dark:text-slate-100 dark:bg-slate-900';
            case 'Approved': return 'text-teal-800 bg-teal-100 dark:text-teal-100 dark:bg-teal-900';
            case 'Production': return 'text-emerald-800 bg-emerald-100 dark:text-emerald-100 dark:bg-emerald-900';
            case 'Rejected': return 'text-red-800 bg-red-100 dark:text-red-100 dark:bg-red-900';
            case 'In Review': return 'text-amber-800 bg-amber-100 dark:text-amber-100 dark:bg-amber-900';
            default: return 'text-gray-800 bg-gray-100 dark:text-gray-100 dark:bg-gray-700';
        }
    }

    openQrDialog(business: DestinationType, templateRef: TemplateRef<any>): void {
        this.selectedQrBusiness = business;
        this.selectedSkin = skins['global'];
        this.generatedLinks = null;

        this._dialog.open(templateRef, {
            width: '95vw',
            maxWidth: '900px',
            height: 'auto',
            maxHeight: '90vh',
            panelClass: 'bg-white'
        });

        this.generateQrLinks();
    }

    generateQrLinks(): void {
        if (!this.selectedQrBusiness || !this.selectedSkin) return;

        this.isGeneratingQr = true;
        this.generatedLinks = null;

        const payload = new FormData();
        payload.append('id_destination', this.selectedQrBusiness.id_destination);

        this._restFactoryService.httpPostService(this.API_GET_ADMIN_QR, payload)
            .pipe(
                take(1),
                finalize(() => {
                    this.isGeneratingQr = false;
                    this._changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: (response: any) => {
                    if (response && response.ds_url && response.ds_label) {
                        const originalUrl = response.ds_url as string;
                        const label = response.ds_label;

                        let mobileUrl = originalUrl;
                        if (this.selectedSkin!.mobileKey) {
                            mobileUrl = originalUrl.replace('global', this.selectedSkin!.mobileKey);
                        }

                        let desktopUrl = this.selectedSkin!.desktopUrl;
                        try {
                            const urlObj = new URL(originalUrl);
                            const params = urlObj.search;
                            desktopUrl = `${desktopUrl}${params}`;
                        } catch (e) {
                            console.error(e);
                        }

                        this.generatedLinks = {
                            mobileUrl,
                            desktopUrl,
                            label
                        };

                        const themeColor = this.selectedSkin?.themeColor || '#000000';
                        const labelImage = this.createLabelImage(label, themeColor);

                        this.mobileQrConfig = { ...this.baseQrConfig, data: mobileUrl, image: labelImage };
                        this.desktopQrConfig = { ...this.baseQrConfig, data: desktopUrl, image: labelImage };
                        this.bigMobileQrConfig = { ...this.baseQrConfig, width: 1024, height: 1024, data: mobileUrl, image: labelImage };
                        this.bigDesktopQrConfig = { ...this.baseQrConfig, width: 1024, height: 1024, data: desktopUrl, image: labelImage };
                    } else {
                        this._snackBar.open('Invalid response from server.', 'Close', { duration: 3000 });
                    }
                },
                error: () => {
                    this._snackBar.open('Error generating link.', 'Close', { duration: 3000 });
                }
            });
    }

    downloadQr(type: 'mobile' | 'desktop'): void {
        const name = (this.selectedQrBusiness?.ds_name || 'qr') + `-${type}.png`;
        const index = type === 'mobile' ? 2 : 3;

        const qrComponent = this.qrComponents.toArray()[index];
        if (qrComponent) {
            qrComponent.download(name);
        }
    }

    copyUrl(url: string | undefined): void {
        if (url) {
            navigator.clipboard.writeText(url).then(() => {
                this._snackBar.open('URL copied!', 'Close', { duration: 2000 });
            });
        }
    }

    private createLabelImage(text: string, color: string): string {
        const size = 1024;
        const padding = 20;
        const borderThickness = 40;
        const center = size / 2;
        const radius = (size / 2) - padding;

        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.fill();

        ctx.lineWidth = borderThickness;
        ctx.strokeStyle = color;
        ctx.stroke();

        const safeWidth = radius * 1.5;
        const safeHeight = radius * 1.5;

        ctx.fillStyle = '#222222';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        let fontSize = 200;
        let lines: string[] = [];
        let lineHeight = 0;
        const minFontSize = 40;

        while (fontSize >= minFontSize) {
            ctx.font = `bold ${fontSize}px "Roboto", "Helvetica Neue", "Arial", sans-serif`;
            lineHeight = fontSize * 1.15;
            lines = this.getWrappedLines(ctx, text, safeWidth);
            const totalHeight = lines.length * lineHeight;
            if (totalHeight <= safeHeight) break;
            fontSize -= 10;
        }

        const totalBlockHeight = lines.length * lineHeight;
        const startY = center - (totalBlockHeight / 2) + (lineHeight / 2) - (fontSize * 0.1);

        lines.forEach((line, index) => {
            ctx.fillText(line, center, startY + (index * lineHeight));
        });

        return canvas.toDataURL();
    }

    private getWrappedLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
        const words = text.split(' ');
        const lines: string[] = [];
        let currentLine = words[0];

        const processToken = (token: string): string[] => {
            if (ctx.measureText(token).width <= maxWidth) return [token];
            const splitByChar = token.split(/([\/,-])/);
            let subLines: string[] = [];
            let subLine = "";

            for (let part of splitByChar) {
                if (ctx.measureText(subLine + part).width <= maxWidth) {
                    subLine += part;
                } else {
                    if (subLine) subLines.push(subLine);
                    if (ctx.measureText(part).width > maxWidth) {
                        let charLine = "";
                        for (let char of part) {
                            if (ctx.measureText(charLine + char).width <= maxWidth) {
                                charLine += char;
                            } else {
                                subLines.push(charLine);
                                charLine = char;
                            }
                        }
                        subLine = charLine;
                    } else {
                        subLine = part;
                    }
                }
            }
            if (subLine) subLines.push(subLine);
            return subLines;
        };

        if (ctx.measureText(currentLine).width > maxWidth) {
            const broken = processToken(currentLine);
            lines.push(...broken.slice(0, -1));
            currentLine = broken[broken.length - 1];
        }

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const testLine = currentLine + " " + word;

            if (ctx.measureText(testLine).width <= maxWidth) {
                currentLine = testLine;
            } else {
                lines.push(currentLine);
                if (ctx.measureText(word).width > maxWidth) {
                    const broken = processToken(word);
                    lines.push(...broken.slice(0, -1));
                    currentLine = broken[broken.length - 1];
                } else {
                    currentLine = word;
                }
            }
        }
        lines.push(currentLine);
        return lines;
    }
}
